import React, { useState, useEffect } from 'react';

import DataTableHeader from './header/header';
import DataTableItem from './item/item';
import Spinner from 'app/shared/spinners/spinner-15/spinner-15';
import ContextMenu from 'app/shared/datatable/context-menu/context-menu';

import '../datatable.css';
import { IconInput } from 'app/shared/input';
import { MoreButton } from 'app/shared/button/more';
import useScreenSize from 'hooks/size';
import { Icon } from 'assets/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'app/hooks/query';
import { TableFilter } from 'app/shared/filter';

const DataTable = ({
  showHeader = true,
  showFooter = true,
  action,
  checkbox,
  config,
  filter_config = [],
  has_filter = false,
  loading_data,
  resetBulkSelection,
  table_actions = [],
  onClick = () => {},
  onDataRequest = (f) => f,
  onSearchRequest = (f) => f
}) => {
  const { fields, items, num_of_rows = 10, search_text, total_count } = config;
  const base_path = window.location.pathname;
  const query = useQuery();
  const navigateTo = useNavigate();

  const [filter_query, setFilterQuery] = useState('');
  const [sort_query, setSortQuery] = useState('');

  const [number_of_rows_to_display] = useState(num_of_rows);
  const [page_number, setPageNumber] = useState(0);

  const [selected_items, setSelectedItems] = useState({});
  const [search_keyword, setSearchKeyword] = useState('');
  const [search_keys, setSearchKeys] = useState('');
  const [show_filters, setShowFilters] = useState(false);
  const [debounce_timer, setDebounceTimer] = useState('');
  const [is_selected, setIsSelected] = useState(false);

  const [table_items, setTableItems] = useState([]);
  const [items_to_display, setItemsToDisplay] = useState([]);
  const { isDesktop } = useScreenSize();

  useEffect(() => {
    buildSearchKeys();

    const { page, q } = query;
    if (q) onSearchRequest(search_keys, q, page || 0, 100, { filter_query, sort_query });

    if (q) setSearchKeyword(q);
    if (page) setPageNumber(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resetBulkSelection) setSelectedItems({});
  }, [resetBulkSelection]);

  useEffect(() => {
    if (debounce_timer) clearTimeout(debounce_timer);
    // debounce search at every 1s
    const timer = setTimeout(
      () => onSearchRequest(search_keys, search_keyword, 0, 100, { filter_query, sort_query }),
      250
    );
    setDebounceTimer(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search_keyword]);

  useEffect(() => {
    const query = [];
    if (page_number) query.push(`page=${page_number}`);
    if (search_keyword) query.push(`q=${search_keyword}`);

    makeRequests(page_number);
    navigateTo(`${base_path}?${query.join('&')}`);
  }, [page_number, search_keyword]);

  useEffect(() => {
    setPageNumber(() => 0);
    makeRequests(0);
  }, [filter_query, sort_query]);

  useEffect(() => {
    const processed_items = items.map((item) => ({
      ...item,
      search_metadata: {
        haystack: Object.values(item).join().toLowerCase(),
        checked: false
      }
    }));

    setTableItems(() => processed_items);
  }, [items]);

  useEffect(() => {
    handleItemsToDisplay();
    formatDataTableFooter(page_number, number_of_rows_to_display, table_items);
  }, [table_items]);

  const buildSearchKeys = () => {
    const keys = [];
    fields.forEach((field) => {
      if (!field.isTitle && !field.isTagline) return;
      keys.push(field.key);
    });
    setSearchKeys(keys.join());
  };

  /** */
  const closeActionMode = () => {
    setSelectedItems(() => ({}));
  };

  const formatDataTableFooter = (page_number, number_of_rows_to_display, items) => {
    const start = page_number ? page_number * number_of_rows_to_display + 1 : 1;
    const end = number_of_rows_to_display + start - 1;
    const content_text_for_empty_data = 'No item(s) to display';
    const content_text_for_nonempty_data = `${start} - ${
      end > total_count ? total_count : end
    } of ${total_count}`;

    return items.length ? content_text_for_nonempty_data : content_text_for_empty_data;
  };

  const formatPagination = () => {
    const high = Math.ceil(total_count / number_of_rows_to_display);
    return (
      <div className="gm-datatable-pagination">
        {loading_data ? (
          <div className="gm-datatable-loader-indicator">
            <Spinner /> <span>Loading</span>
          </div>
        ) : (
          <></>
        )}
        {page_number > 0 ? (
          <button onClick={() => handlePageChange(-1)}> {'Prev'} </button>
        ) : (
          <button disabled> {'Prev'} </button>
        )}
        {page_number < high - 1 ? (
          <button onClick={() => handlePageChange(1)}> {'Next'} </button>
        ) : (
          <button disabled> {'Next'} </button>
        )}
      </div>
    );
  };

  const handleItemClick = (item_data) => {
    onClick(item_data);
  };

  const handleItemsToDisplay = () => {
    const start = page_number * number_of_rows_to_display;
    const end = start + number_of_rows_to_display;

    setItemsToDisplay(() => [...table_items].slice(start, end));
  };

  const handlePageChange = (value) => {
    const new_page_num = Number(page_number) + Number(value);
    setPageNumber(() => new_page_num);

    makeRequests(new_page_num);
  };

  const handleSearch = async (keyword) => {
    setSearchKeyword(keyword);
  };

  const handleSelection = (selected_item) => {
    setSelectedItems((selected_items) => ({
      ...selected_items,
      [selected_item.id]: selected_item
    }));
  };

  const handleUnselection = (id) => {
    const selections = { ...selected_items };
    delete selections[id];
    setSelectedItems(() => selections);
  };

  const handleSort = (field, is_ascending) => {
    setSortQuery(`sort_by=${!is_ascending ? '-' : ''}${field}`);
  };

  const makeRequests = async (page_num = 0) => {
    const reset = filter_query === '&';
    if (search_keyword)
      onSearchRequest(search_keys, search_keyword, page_num, 100, {
        filter_query,
        sort_query,
        reset
      });
    else onDataRequest(page_num, 100, { filter_query, sort_query, reset });
  };

  const processAction = async (name, data) => {
    if (data && data.id) {
      // it is a single action
      action({
        name,
        type: 'single',
        data
      });
      return;
    }

    if (!Object.keys(selected_items).length) return;

    action({
      name,
      type: 'bulk',
      data: Object.values(selected_items)
    });
  };

  const toggleBulkSelection = async () => {
    if (checkBulkMode()) {
      closeActionMode();
      return;
    }

    await new Promise((resolve, reject) => setTimeout(resolve, 500));

    const source = items_to_display;
    const selections = source.reduce((sack, result) => {
      if (!result.is_active) return sack;
      return {
        ...sack,
        [result.id]: result
      };
    }, {});
    setSelectedItems(() => selections);
  };

  const checkBulkMode = () => {
    return Object.keys(selected_items).length === items_to_display.length;
  };

  return (
    <div className="gm-datatable">
      {showHeader && (
        <section className="gm-datatable-header">
          <div className="gm-datatable-search">
            <IconInput
              icon_name="search"
              placeholder={search_text || 'Search'}
              onInput={(value) => handleSearch(value)}
              value={search_keyword}
              wrapperClassName="search-input"
            />
            {has_filter && <Icon name="filter" onClick={() => setShowFilters(true)} />}
          </div>
          <div className="gm-datatable-actions">
            {table_actions}
            <ContextMenu
              actions={config.actions.bulk}
              callback={processAction}
              text={
                isDesktop ? (
                  <MoreButton text="Actions" disabled={!config.actions.bulk} />
                ) : (
                  <Icon name="more_vert" />
                )
              }
            />
          </div>
        </section>
      )}
      <section className="gm-datatable-table">
        <table>
          <thead>
            <DataTableHeader
              actions={config.actions.bulk}
              checkbox={checkbox}
              data={fields}
              sort_callback={handleSort}
              onChange={toggleBulkSelection}
              unselect={resetBulkSelection}
              is_selected={is_selected}
              setIsSelected={setIsSelected}
              is_bulk_mode={checkBulkMode()}
              show_action_column={!!config.actions.single}
            />
          </thead>
          <tbody>
            {items_to_display.map((item) => (
              <DataTableItem
                actions={config.actions.single}
                action_callback={processAction}
                checkbox={config.actions.bulk}
                data={item}
                fields={fields}
                index={item.id}
                item_click_callback={handleItemClick}
                key={item.id}
                bulk_selection={selected_items}
                selection_callback={handleSelection}
                unselection_callback={handleUnselection}
                deselect={!Object.keys(selected_items).length}
              />
            ))}
          </tbody>
        </table>
        {loading_data && <div className="gm-datatable-full-loader">loading data</div>}
      </section>
      {showFooter && (
        <section className="gm-datatable-footer">
          <div>{formatDataTableFooter(page_number, number_of_rows_to_display, table_items)}</div>
          {formatPagination()}
        </section>
      )}
      <TableFilter
        config={filter_config}
        is_open={show_filters}
        onApply={setFilterQuery}
        onClose={() => setShowFilters(false)}
      />
    </div>
  );
};

export default DataTable;
